import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
  IonAlert,
  IonText,
  IonIcon,
  IonRadioGroup,
  IonRadio,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonRow,
  IonCol,
  IonGrid,
  IonDatetime,
  IonCheckbox,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { 
  arrowBack,
  close,
  pencilOutline, 
  trash, 
  checkmarkOutline, 
  closeOutline, 
  lockClosedOutline, 
  lockOpenOutline, 
  refreshOutline,
  trashOutline 
} from 'ionicons/icons';
import { planningService } from '@/services/PlanningService';
import { UpdateInspectionPlanRequest, AutoInspections } from '@/types/request/InspectionPlanningRequests';
import { FileAttachment } from '@/types/model/inspection-modal';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import { InspectionMethodsResponse, ExpertiseAreasResponse, FrequenciesResponse } from '@/types/response/lookupResponses';
import AttachmentsSection from '@/components/common/AttachmentsSection';
import { apiService } from '@/services/ApiService';
import permissionService from '@/services/PermissionService';
import ModalFooter from '@/components/common/ModalFooter';
import { buildAttachmentOperations } from '@/services/util/attachmentOperations';
import {
  mapPlanningAttachments,
  findPlanningFrequencyId,
  formatPlanningDate,
  validateActiveInspectionPlanRules,
  validateInspectionPlanForm,
} from '@/services/util/planningUtils';

interface ChecklistItem {
  id?: number;
  text: string;
  weight: number;
  resultsAnalysis?: string;
}

interface FixedInspectionDate {
  date: string;
  isAnnual: boolean;
  site?: string;
}

interface ScheduledInspection {
  id: number;
  site: string;
  date: string;
  frequencyId: number;
}

interface FrequencyLevel {
  level: 'normal' | 'decreased' | 'increased';
  frequencyId: number;
  low?: number;
  high?: number;
}

interface InspectionPlanModalProps {
  isOpen: boolean;
  mode: 'view' | 'edit';
  onClose: () => void;
  onSave?: () => void;
  onRefresh?: () => Promise<void>;
  initialData?: {
    id: number;
    lineItemId: number;
    lineItemTitle: string;
    lineItemIdentifier: string;
    primaryValue?: string;
    alternateValue?: string;
    timeToInspect?: string;
    inspectionMethodId?: number;
    expertiseAreaId?: number;
    sampleSize?: string;
    sampleSelection?: string;
    additionalInstructions?: string;
    isActive?: boolean;
    isLocked?: boolean;
    isLineItemActive?: boolean;
    attachments?: Array<{
      Id: number;
      Description: string;
      Link: string;
      FileId?: number;
      Filename?: string;
    }>;
    autoInspections?: {
      normalFrequencyId: number;
      normalLow?: number;
      normalHigh?: number;
      decreasedFrequencyId: number;
      decreasedLow?: number;
      increasedFrequencyId: number;
      increasedHigh?: number;
    };
    scheduledInspections?: ScheduledInspection[];
    fixedInspections?: FixedInspectionDate[];
    checklistItems?: ChecklistItem[];
    majorRequirement?: string;
    relatedInfo?: string;
    estimatedQuantity?: string;
    performanceStandard?: string;
  };
  existingFiles: FileResponse[];
}

const InspectionPlanModal: React.FC<InspectionPlanModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSave,
  onRefresh,
  initialData,
  existingFiles,
}) => {
  const [formData, setFormData] = useState({
    primaryValue: '',
    alternateValue: '',
    timeToInspect: '',
    inspectionMethodId: 0,
    expertiseAreaId: 0,
    sampleSize: '',
    sampleSelection: '',
    additionalInstructions: '',
  });

  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [internalMode, setInternalMode] = useState<'view' | 'edit'>(mode);
  const [originalFormData, setOriginalFormData] = useState(formData);

  // Lookup data
  const [inspectionMethods, setInspectionMethods] = useState<InspectionMethodsResponse[]>([]);
  const [expertiseAreas, setExpertiseAreas] = useState<ExpertiseAreasResponse[]>([]);
  const [frequencies, setFrequencies] = useState<FrequenciesResponse[]>([]);

  // Frequency settings
  const [frequencyMode, setFrequencyMode] = useState<'auto' | 'profile' | 'fixed'>('auto');
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [frequencyLevels, setFrequencyLevels] = useState<FrequencyLevel[]>([
    { level: 'normal', frequencyId: 0, low: 4, high: 6 },
    { level: 'decreased', frequencyId: 0, low: 6 },
    { level: 'increased', frequencyId: 0, high: 4 },
  ]);

  // Checklist items
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);

  // Scheduled inspections
  const [scheduledInspections, setScheduledInspections] = useState<ScheduledInspection[]>([]);

  // Fixed inspection dates
  const [currentDate, setCurrentDate] = useState<string>(new Date().toISOString());
  const [isCurrentDateAnnual, setIsCurrentDateAnnual] = useState<boolean>(false);
  const [fixedInspectionDates, setFixedInspectionDates] = useState<FixedInspectionDate[]>([]);
  const [removeOldNonAnnual, setRemoveOldNonAnnual] = useState<boolean>(false);

  // Action state
  const [showActionConfirmation, setShowActionConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'activate' | 'deactivate' | 'lock' | 'unlock' | 'reset';
    label: string;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [showLookupSetupAlert, setShowLookupSetupAlert] = useState(false);
  const [lookupSetupAlertMessage, setLookupSetupAlertMessage] = useState('');

  // Load lookup data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadLookupData();
    }
  }, [isOpen]);

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      setInternalMode(mode); // Reset internal mode when modal opens
      if (initialData) {
        const newFormData = {
          primaryValue: initialData.primaryValue || '',
          alternateValue: initialData.alternateValue || '',
          timeToInspect: initialData.timeToInspect || '',
          inspectionMethodId: initialData.inspectionMethodId || 0,
          expertiseAreaId: initialData.expertiseAreaId || 0,
          sampleSize: initialData.sampleSize || '',
          sampleSelection: initialData.sampleSelection || '',
          additionalInstructions: initialData.additionalInstructions || '',
        };
        setFormData(newFormData);
        setOriginalFormData(newFormData); // Store original for cancel functionality

        if (initialData.attachments && initialData.attachments.length > 0) {
          setAttachments(mapPlanningAttachments(initialData.attachments));
        } else {
          setAttachments([]);
        }

        // Set frequency levels if available
        if (initialData.autoInspections) {
          setFrequencyLevels([
            {
              level: 'normal',
              frequencyId: initialData.autoInspections.normalFrequencyId || 0,
              low: initialData.autoInspections.normalLow,
              high: initialData.autoInspections.normalHigh,
            },
            {
              level: 'decreased',
              frequencyId: initialData.autoInspections.decreasedFrequencyId || 0,
              low: initialData.autoInspections.decreasedLow,
            },
            {
              level: 'increased',
              frequencyId: initialData.autoInspections.increasedFrequencyId || 0,
              high: initialData.autoInspections.increasedHigh,
            },
          ]);
        }

        // Set checklist items if available
        if (initialData.checklistItems) {
          setChecklistItems(initialData.checklistItems);
        } else {
          setChecklistItems([]);
        }

        // Set scheduled inspections if available
        if (initialData.scheduledInspections) {
          setScheduledInspections(initialData.scheduledInspections);
        } else {
          setScheduledInspections([]);
        }

        // Set fixed inspection dates and derive frequency mode
        if (initialData.fixedInspections && initialData.fixedInspections.length > 0) {
          setFixedInspectionDates(initialData.fixedInspections);
          setFrequencyMode('fixed');
        } else {
          setFixedInspectionDates([]);
          setFrequencyMode(frequencies.length === 0 ? 'fixed' : 'auto');
        }
      } else {
        // Reset to empty form
        const emptyFormData = {
          primaryValue: '',
          alternateValue: '',
          timeToInspect: '',
          inspectionMethodId: 0,
          expertiseAreaId: 0,
          sampleSize: '',
          sampleSelection: '',
          additionalInstructions: '',
        };
        setFormData(emptyFormData);
        setOriginalFormData(emptyFormData);
        setAttachments([]);
        setChecklistItems([]);
        setScheduledInspections([]);
        setFixedInspectionDates([]);
        setFrequencyMode(frequencies.length === 0 ? 'fixed' : 'auto');
      }
      setValidationErrors([]);
    }
  }, [isOpen, initialData, mode, frequencies.length]);

  const loadLookupData = async () => {
    try {
      const [methodsData, expertiseData, frequencyData] = await Promise.all([
        apiService.getInspectionMethods(),
        apiService.getContractAreasOfExpertise(),
        apiService.getContractFrequencies(),
      ]);
      setInspectionMethods(methodsData);
      setExpertiseAreas(expertiseData);
      setFrequencies(frequencyData);

      const missingLookupItems: string[] = [];
      if (methodsData.length === 0) {
        missingLookupItems.push('Inspection Methods');
      }
      if (expertiseData.length === 0) {
        missingLookupItems.push('Area of Expertise');
      }
      if (frequencyData.length === 0) {
        missingLookupItems.push('Frequencies');
      }

      if (missingLookupItems.length > 0) {
        const lookupSummary = missingLookupItems.join(', ');
        setLookupSetupAlertMessage(`${lookupSummary} ${missingLookupItems.length === 1 ? 'is' : 'are'} not set up.`);
        setShowLookupSetupAlert(true);
      } else {
        setShowLookupSetupAlert(false);
        setLookupSetupAlertMessage('');
      }

      if (frequencyData.length === 0) {
        setFrequencyMode('fixed');
      }
    } catch (error) {
      console.error('Error loading lookup data:', error);
    }
  };

  const hasChanges = (): boolean => {
    if (internalMode === 'view') return false;

    if (initialData && internalMode === 'edit') {
      // For edit mode, compare with original values
      const hasFormChanges = 
        formData.primaryValue !== originalFormData.primaryValue ||
        formData.alternateValue !== originalFormData.alternateValue ||
        formData.timeToInspect !== originalFormData.timeToInspect ||
        formData.inspectionMethodId !== originalFormData.inspectionMethodId ||
        formData.expertiseAreaId !== originalFormData.expertiseAreaId ||
        formData.sampleSize !== originalFormData.sampleSize ||
        formData.sampleSelection !== originalFormData.sampleSelection ||
        formData.additionalInstructions !== originalFormData.additionalInstructions;

      // FIXME! - enhance to check individual items
      const hasAttachmentChanges = attachments.length !== (initialData.attachments?.length || 0);
      const hasChecklistChanges = checklistItems.length !== (initialData.checklistItems?.length || 0);
      const hasScheduledInspectionChanges = scheduledInspections.length !== (initialData.scheduledInspections?.length || 0);

      return hasFormChanges || hasAttachmentChanges || hasChecklistChanges || hasScheduledInspectionChanges;
    }

    return false;
  };

  const validateForm = (): string[] => validateInspectionPlanForm({
    checklistItems,
    frequencyMode,
    frequencyLevels,
  });

  const validateActivePlanRules = (): string[] => {
    return validateActiveInspectionPlanRules({
      checklistItems,
      frequencyMode,
      frequencyLevels,
      expertiseAreaId: formData.expertiseAreaId,
      primaryValue: formData.primaryValue,
      alternateValue: formData.alternateValue,
      fixedInspectionDateCount: fixedInspectionDates.length,
    });
  };

  const handleClose = () => {
    if (hasChanges() && internalMode !== 'view') {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleEdit = () => {
    setInternalMode('edit');
  };

  const handleCancel = () => {
    if (hasChanges()) {
      setShowConfirmClose(true);
    } else {
      // Revert to original data and go back to view mode
      setFormData(originalFormData);
      setInternalMode('view');
    }
  };

  const handleSave = async () => {
    const errors = validateForm();
    const activePlanErrors = (initialData?.isActive ?? false) ? validateActivePlanRules() : [];
    const allErrors = [...errors, ...activePlanErrors];
    if (allErrors.length > 0) {
      setValidationErrors(allErrors);
      return;
    }

    setIsLoading(true);
    setValidationErrors([]);

    try {
      if (!initialData) {
        throw new Error('No initial data provided for inspection plan update');
      }

      console.log('frequency mode:', frequencyMode);

      // Derive AutoInspections and FixedInspections based on frequency mode
      const autoInspectionsValue: AutoInspections | null = frequencyMode === 'auto'
        ? {
            NormalFrequencyId: frequencyLevels.find(f => f.level === 'normal')?.frequencyId || 0,
            NormalLow: frequencyLevels.find(f => f.level === 'normal')?.low ?? null,
            NormalHigh: frequencyLevels.find(f => f.level === 'normal')?.high ?? null,
            DecreasedFrequencyId: frequencyLevels.find(f => f.level === 'decreased')?.frequencyId || 0,
            DecreasedLow: frequencyLevels.find(f => f.level === 'decreased')?.low ?? null,
            IncreasedFrequencyId: frequencyLevels.find(f => f.level === 'increased')?.frequencyId || 0,
            IncreasedHigh: frequencyLevels.find(f => f.level === 'increased')?.high ?? null,
          }
        : null;

      const fixedInspectionsValue = frequencyMode === 'fixed'
        ? fixedInspectionDates.map(d => ({ Date: d.date, IsAnnual: d.isAnnual }))
        : [];

      console.log('Derived auto inspections value:', autoInspectionsValue);
      console.log('Derived fixed inspections value:', fixedInspectionsValue);

      const uploadedFileIds = new Map<FileAttachment, number>();
      for (const attachment of attachments) {
        if (!attachment.fileId && attachment.file) {
          const uploadResult = await apiService.uploadFile(attachment);
          uploadedFileIds.set(attachment, uploadResult.FileId);
        }
      }

      const updateRequest: UpdateInspectionPlanRequest = {
        Id: initialData.id,
        LineItem: initialData.lineItemIdentifier,
        Title: initialData.lineItemTitle,
        LineItemLastUpdate: new Date().toISOString(),
        InspectionMethodId: formData.inspectionMethodId || null,
        ExpertiseAreaId: formData.expertiseAreaId || null,
        PrimaryValue: formData.primaryValue,
        AlternateValue: formData.alternateValue,
        TimeToInspect: formData.timeToInspect,
        SampleSize: formData.sampleSize,
        SampleSelection: formData.sampleSelection,
        AdditionalInstructions: formData.additionalInstructions,
        IsActive: initialData.isActive ?? true,
        IsLocked: initialData.isLocked ?? false,
        IsLineItemActive: true,
        Status: (initialData.isActive ?? true) ? 'Active' : 'Inactive',
        MajorRequirement: initialData.majorRequirement || '',
        RelatedInfo: initialData.relatedInfo || '',
        EstimatedQuantity: initialData.estimatedQuantity || '',
        PerformanceStandard: initialData.performanceStandard || '',
        AutoInspections: autoInspectionsValue,
        FixedInspections: fixedInspectionsValue,
        ChecklistItems: checklistItems.map((item) => ({
          Id: item.id || 0,
          Text: item.text,
          ResultsAnalysis: item.resultsAnalysis || '',
          Weight: item.weight.toString(),
        })),
        Attachments: buildAttachmentOperations({
          attachments,
          originalAttachments: initialData.attachments ? mapPlanningAttachments(initialData.attachments) : [],
          uploadedFileIds,
        }),
        ScheduledInspectionsCount: scheduledInspections.length,
        ScheduledInspections: scheduledInspections.map((inspection) => ({
          Id: inspection.id,
          Site: inspection.site,
          Date: inspection.date,
          FrequencyId: inspection.frequencyId,
        })),
        InspectionPlanLastUpdate: new Date().toISOString(),
        NormalHoursToInspect: parseInt(formData.timeToInspect) || null,
        CurrentHoursToInspect: parseInt(formData.timeToInspect) || null,
        AnnualFixedInspectionCount: fixedInspectionDates.filter(d => d.isAnnual).length,
        AttachmentCount: attachments.length,
        LineItemAttachments: [],
        RemoveOldNonAnnual: removeOldNonAnnual,
        sectionId: '',
      };

      await planningService.updateInspectionPlan(updateRequest);

      await onSave?.();
      setOriginalFormData(formData);
      setInternalMode('view');
      onClose();
    } catch (error) {
      console.error('Error saving inspection plan:', error);
      setValidationErrors([error instanceof Error ? error.message : 'Failed to save inspection plan. Please try again.']);
    } finally {
      setIsLoading(false);
    }
  };

  const getReadOnlyStyle = () => {
    if (internalMode === 'view') {
      return {
        '--background': 'var(--ion-color-light)',
        '--color': 'var(--ion-color-medium-shade)',
        '--border-color': 'var(--ion-color-light-shade)',
        '--placeholder-color': 'var(--ion-color-medium-tint)',
        cursor: 'default',
        opacity: '0.8'
      };
    }
    return {};
  };

  // Attachment management functions
  const addAttachment = (type: 'local-file' | 'existing-resource' | 'link' | 'image') => {
    const newAttachment: FileAttachment = {
      type,
      index: attachments.length,
    };
    setAttachments([...attachments, newAttachment]);
  };

  const handleAttachmentDelete = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleFileUpdate = (index: number, file: File) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], file, fileName: file.name, size: file.size };
    setAttachments(updated);
  };

  const handleLinkUpdate = (index: number, link: string) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], link };
    setAttachments(updated);
  };

  const handleExistingFileSelect = (index: number, fileId: string) => {
    const updated = [...attachments];
    const selectedFile = existingFiles.find(f => f.Id.toString() === fileId);
    updated[index] = { 
      ...updated[index], 
      fileId, 
      fileName: selectedFile?.Name 
    };
    setAttachments(updated);
  };

  const handleDescriptionUpdate = (index: number, description: string) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], description };
    setAttachments(updated);
  };

  // Checklist item management
  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, { text: '', weight: 1 }]);
  };

  const updateChecklistItem = (index: number, field: keyof ChecklistItem, value: string | number) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], [field]: value };
    setChecklistItems(updated);
  };

  const removeChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  // Frequency level management
  const updateFrequencyLevel = (level: 'normal' | 'decreased' | 'increased', field: string, value: number) => {
    setFrequencyLevels(prev => prev.map(item => 
      item.level === level ? { ...item, [field]: value } : item
    ));
  };

  // Fixed inspection date management
  const addFixedInspectionDate = () => {
    if (!currentDate) {
      setToastMessage('Please choose a date before adding it.');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    const newDate: FixedInspectionDate = {
      date: currentDate,
      isAnnual: isCurrentDateAnnual,
    };
    
    // Check if date already exists
    const exists = fixedInspectionDates.some(existing => 
      existing.date.split('T')[0] === newDate.date.split('T')[0]
    );
    
    if (!exists) {
      setFixedInspectionDates([...fixedInspectionDates, newDate]);
      // Reset the form
      setCurrentDate(new Date().toISOString());
      setIsCurrentDateAnnual(false);
    } else {
      setToastMessage('This date has already been added.');
      setToastVariant('warning');
      setShowToast(true);
    }
  };

  const removeFixedInspectionDate = (index: number) => {
    setFixedInspectionDates(fixedInspectionDates.filter((_, i) => i !== index));
  };

  const formatDateForDisplay = formatPlanningDate;

  // Predefined frequency profiles
  const frequencyProfiles = {
    critical: {
      name: 'Critical',
      levels: {
        normal: { frequencyName: 'Monthly', low: 7, high: 9 },
        decreased: { frequencyName: 'Quarterly', low: 8, high: undefined },
        increased: { frequencyName: 'Weekly', low: undefined, high: 8 }
      }
    },
    monthly: {
      name: 'Monthly',
      levels: {
        normal: { frequencyName: 'Monthly', low: 4, high: 6 },
        decreased: { frequencyName: 'Quarterly', low: 6, high: undefined },
        increased: { frequencyName: 'Weekly', low: undefined, high: 4 }
      }
    }
  };

  // Helper function to find frequency ID by name
  const findFrequencyIdByName = (name: string): number => findPlanningFrequencyId(frequencies, name);

  // Apply selected profile
  const applyFrequencyProfile = (profileKey: string) => {
    if (!profileKey || !frequencyProfiles[profileKey as keyof typeof frequencyProfiles]) {
      return;
    }

    const profile = frequencyProfiles[profileKey as keyof typeof frequencyProfiles];
    const newFrequencyLevels: FrequencyLevel[] = [
      {
        level: 'normal',
        frequencyId: findFrequencyIdByName(profile.levels.normal.frequencyName),
        low: profile.levels.normal.low,
        high: profile.levels.normal.high
      },
      {
        level: 'decreased', 
        frequencyId: findFrequencyIdByName(profile.levels.decreased.frequencyName),
        low: profile.levels.decreased.low,
        high: profile.levels.decreased.high
      },
      {
        level: 'increased',
        frequencyId: findFrequencyIdByName(profile.levels.increased.frequencyName), 
        low: profile.levels.increased.low,
        high: profile.levels.increased.high
      }
    ];

    setFrequencyLevels(newFrequencyLevels);
  };

  // Handle profile selection  
  const handleProfileSelection = (profileKey: string) => {
    setSelectedProfile(profileKey);
    if (profileKey) {
      applyFrequencyProfile(profileKey);
    }
  };

  // Action handlers
  const handleActionClick = (action: 'activate' | 'deactivate' | 'lock' | 'unlock' | 'reset') => {
    if (!initialData) return;

    // Check constraints
    if (action === 'activate') {
      const activationErrors = validateActivePlanRules();
      if (activationErrors.length > 0) {
        setValidationErrors(activationErrors);
        return;
      }
    }

    // Set pending action and show confirmation
    const actionLabels = {
      activate: 'Activate',
      deactivate: 'Deactivate', 
      lock: 'Lock',
      unlock: 'Unlock',
      reset: 'Reset'
    };

    setPendingAction({
      type: action,
      label: actionLabels[action]
    });
    setShowActionConfirmation(true);
  };

  const handleActionConfirm = async () => {
    if (!pendingAction || !initialData) return;

    setIsLoading(true);
    setShowActionConfirmation(false);

    try {
      const inspectionPlanId = initialData.id;
      
      switch (pendingAction.type) {
        case 'activate':
          await planningService.activateDeactivateInspectionPlan([inspectionPlanId], []);
          setToastMessage('Inspection plan activated successfully');
          break;
        case 'deactivate':
          await planningService.activateDeactivateInspectionPlan([], [inspectionPlanId]);
          setToastMessage('Inspection plan deactivated successfully');
          break;
        case 'lock':
          await planningService.lockUnlockInspectionPlan([inspectionPlanId], [], 'User requested lock');
          setToastMessage('Inspection plan locked successfully');
          break;
        case 'unlock':
          await planningService.lockUnlockInspectionPlan([], [inspectionPlanId], 'User requested unlock');
          setToastMessage('Inspection plan unlocked successfully');
          break;
        case 'reset':
          await planningService.resetInspectionPlan([inspectionPlanId]);
          setToastMessage('Inspection plan reset successfully');
          break;
      }

      setToastVariant('success');
      setShowToast(true);
      if (onRefresh) {
        try {
          await onRefresh();
        } catch (refreshError) {
          console.error('Failed to refresh planning data after inspection plan action:', refreshError);
        }
      }
      onClose(); // Close modal after successful action
    } catch (error) {
      console.error('Action failed:', error);
      setToastMessage(`Failed to ${pendingAction.label.toLowerCase()} inspection plan`);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false);
      setPendingAction(null);
    }
  };

  const handleActionCancel = () => {
    setShowActionConfirmation(false);
    setPendingAction(null);
  };

  const isActionDisabled = (action: 'activate' | 'deactivate' | 'lock' | 'unlock' | 'reset'): boolean => {
    if (!initialData) return true;
    
    // Actions are disabled in edit mode
    if (internalMode === 'edit') return true;
    
    // When locked, only unlock is allowed (status check)
    if (initialData.isLocked && action !== 'unlock') return true;

    // Permission checks
    const isLineItemActive = initialData.isLineItemActive ?? false;
    switch (action) {
      case 'activate':
      case 'deactivate':
        return !permissionService.canActivateInspectionPlan(initialData.isLocked ?? false);
      case 'lock':
        return !permissionService.canLockInspectionPlan(isLineItemActive);
      case 'unlock':
        return !permissionService.canUnlockInspectionPlan(isLineItemActive);
      case 'reset':
        return !permissionService.canResetInspectionPlan(initialData.isLocked ?? false);
      default:
        return false;
    }
  };

  return (
    <>
      <IonModal
        isOpen={isOpen}
        onDidDismiss={handleClose}
        style={{
          '--width': 'min(80vw, 1400px)',
          '--max-width': '1400px',
          '--height': '90vh'
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{initialData?.lineItemIdentifier} {initialData?.lineItemTitle}</IonTitle>
            
            <IonButtons slot="end">
              {internalMode === 'view' && permissionService.canEditInspectionPlan(initialData?.isLocked ?? false) && (
                <IonButton fill="clear" onClick={handleEdit}>
                  <IonIcon icon={pencilOutline} />
                </IonButton>
              )}
              <IonButton fill="clear" onClick={handleClose}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          {/* Action Icons */}
          <IonCard style={{ width: '100%', maxWidth: '100%' }}>
            <IonCardContent>
              <IonGrid>
                <IonRow>
                  <IonCol size="12">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <IonButton
                          fill="clear"
                          size="default"
                          disabled={isActionDisabled(initialData?.isActive ? 'deactivate' : 'activate')}
                          onClick={() => handleActionClick(initialData?.isActive ? 'deactivate' : 'activate')}
                          title={initialData?.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <IonIcon 
                            icon={initialData?.isActive ? closeOutline : checkmarkOutline} 
                            color={isActionDisabled(initialData?.isActive ? 'deactivate' : 'activate') ? 'medium' : (initialData?.isActive ? 'warning' : 'success')}
                          />
                        </IonButton>

                        <IonButton
                          fill="clear"
                          size="default"
                          disabled={isActionDisabled(initialData?.isLocked ? 'unlock' : 'lock')}
                          onClick={() => handleActionClick(initialData?.isLocked ? 'unlock' : 'lock')}
                          title={initialData?.isLocked ? 'Unlock' : 'Lock'}
                        >
                          <IonIcon 
                            icon={initialData?.isLocked ? lockOpenOutline : lockClosedOutline} 
                            color={isActionDisabled(initialData?.isLocked ? 'unlock' : 'lock') ? 'medium' : 'primary'}
                          />
                        </IonButton>

                        <IonButton
                          fill="clear"
                          size="default"
                          disabled={isActionDisabled('reset')}
                          onClick={() => handleActionClick('reset')}
                          title="Reset"
                        >
                          <IonIcon 
                            icon={refreshOutline} 
                            color={isActionDisabled('reset') ? 'medium' : 'secondary'}
                          />
                        </IonButton>
                      </div>
                      
                      
                      {internalMode === 'edit' && (
                        <IonText color="medium" style={{ fontSize: '0.85rem' }}>
                          <p style={{ fontStyle: 'italic' }}>Cancel edit to enable actions</p>
                        </IonText>
                      )}

                      {/* Action Confirmation Box */}
                      {showActionConfirmation && pendingAction && (
                        <div style={{ 
                          padding: '12px 16px', 
                          backgroundColor: 'var(--ion-color-light)', 
                          borderRadius: '8px', 
                          border: '1px solid var(--ion-color-medium)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <IonText>
                            <span style={{ fontWeight: 'bold' }}>Confirm {pendingAction.label}</span>
                          </IonText>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <IonButton size="small" fill="solid" color="primary" onClick={handleActionConfirm}>
                              OK
                            </IonButton>
                            <IonButton size="small" fill="outline" color="medium" onClick={handleActionCancel}>
                              Cancel
                            </IonButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </IonCard>

          {/* Checklist Items Section */}
          <IonCard style={{ marginTop: '16px', width: '100%', maxWidth: '100%' }}>
            <IonCardHeader>
              <IonCardTitle>Checklist Items</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {/* Checklist Headers */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '50px 1fr 100px 50px',
                gap: '8px', 
                fontWeight: 'bold',
                marginBottom: '16px',
                fontSize: '14px',
                color: 'var(--ion-color-medium)'
              }}>
                <div>#</div>
                <div>Question/Scoring</div>
                <div>Weight</div>
                <div></div>
              </div>

              {/* Checklist Items */}
              {checklistItems.map((item, index) => (
                <div key={index} style={{ 
                  display: 'grid',
                  gridTemplateColumns: '50px 1fr 100px 50px',
                  gap: '8px',
                  alignItems: 'start',
                  marginBottom: '12px',
                  paddingBottom: '12px',
                  borderBottom: index < checklistItems.length - 1 ? '1px solid var(--ion-color-light-shade)' : 'none'
                }}>
                  <div style={{ paddingTop: '8px', fontSize: '14px' }}>{index + 1}</div>
                  <IonTextarea
                    value={item.text}
                    onIonInput={(e) => updateChecklistItem(index, 'text', e.detail.value!)}
                    placeholder="Enter question or scoring criteria"
                    rows={2}
                    maxlength={150}
                    readonly={internalMode === 'view'}
                    style={getReadOnlyStyle()}
                  />
                  <IonInput
                    value={item.weight}
                    onIonInput={(e) => updateChecklistItem(index, 'weight', parseFloat(e.detail.value!) || 0)}
                    type="number"
                    step="0.01"
                    readonly={internalMode === 'view'}
                    style={getReadOnlyStyle()}
                  />
                  {internalMode !== 'view' && (
                    <IonButton
                      fill="clear"
                      color="danger"
                      onClick={() => removeChecklistItem(index)}
                    >
                      <IonIcon icon={trash} />
                    </IonButton>
                  )}

                  <div></div>
                  <div style={{ gridColumn: '2 / 4' }}>
                    <IonLabel position="stacked" style={{ marginBottom: '5px', fontSize: '0.85rem' }}>
                      Scoring Criteria
                    </IonLabel>
                    <IonTextarea
                      value={item.resultsAnalysis || ''}
                      onIonInput={(e) => updateChecklistItem(index, 'resultsAnalysis', e.detail.value || '')}
                      placeholder="Enter scoring criteria"
                      rows={2}
                      maxlength={300}
                      readonly={internalMode === 'view'}
                      style={getReadOnlyStyle()}
                    />
                  </div>
                  <div></div>
                </div>
              ))}

              {checklistItems.length === 0 && (
                <IonText color="medium">
                  <p style={{ fontStyle: 'italic' }}>No checklist items</p>
                </IonText>
              )}

              {/* Add Checklist Item Button */}
              {internalMode !== 'view' && (
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <IonButton
                    onClick={addChecklistItem}
                    fill="outline"
                    size="small"
                    color="primary"
                  >
                    NEW
                  </IonButton>
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Frequency Section */}
          <IonCard style={{ marginTop: '16px', width: '100%', maxWidth: '100%' }}>
            <IonCardHeader>
              <IonCardTitle>Frequency</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonRadioGroup
                value={frequencyMode}
                onIonChange={e => setFrequencyMode(e.detail.value === 'auto' && frequencies.length === 0 ? 'fixed' : e.detail.value)}
              >
                <IonItem
                  style={frequencies.length === 0 ? {
                    '--background': 'var(--ion-color-light)',
                    opacity: '0.55'
                  } : undefined}
                >
                  <IonLabel style={frequencies.length === 0 ? { color: 'var(--ion-color-medium)' } : undefined}>Auto-adjusted Frequencies</IonLabel>
                  <IonRadio slot="start" value="auto" disabled={internalMode === 'view' || frequencies.length === 0} />
                </IonItem>
                <IonItem>
                  <IonLabel>Fixed Inspection Dates</IonLabel>
                  <IonRadio slot="start" value="fixed" disabled={internalMode === 'view'} />
                </IonItem>
              </IonRadioGroup>

              {frequencyMode === 'auto' && (
                <div style={{ marginTop: '20px' }}>
                  {/* Profile Selection */}
                  <div style={{ marginBottom: '24px' }}>
                    <IonText>
                      <h4 style={{ margin: '0 0 12px 0', color: 'var(--ion-color-medium)' }}>Apply Predefined Profile</h4>
                    </IonText>
                    <IonSelect
                      value={selectedProfile}
                      onIonChange={(e) => handleProfileSelection(e.detail.value)}
                      placeholder="Select a frequency profile"
                      disabled={internalMode === 'view'}
                      style={{ 
                        '--background': 'var(--ion-color-light)',
                        '--border-radius': '8px',
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        minWidth: '200px'
                      }}
                    >
                      <IonSelectOption value="critical">
                        Critical (Normal: Monthly 7-9, Decreased: Quarterly 8+, Increased: Weekly &lt;8)
                      </IonSelectOption>
                      <IonSelectOption value="monthly">
                        Monthly (Normal: Monthly 4-6, Decreased: Quarterly 6+, Increased: Weekly &lt;4)
                      </IonSelectOption>
                    </IonSelect>
                  </div>

                  {/* Frequency Headers */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '100px 150px 100px 100px',
                    gap: '8px', 
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: 'var(--ion-color-medium)'
                  }}>
                    <div>Level</div>
                    <div>Frequency</div>
                    <div>Low</div>
                    <div>High</div>
                  </div>

                  {/* Normal Level */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '100px 150px 100px 100px',
                      gap: '8px',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontSize: '14px' }}>Normal</div>
                      <IonSelect
                        value={frequencyLevels.find(f => f.level === 'normal')?.frequencyId}
                        onIonChange={(e) => updateFrequencyLevel('normal', 'frequencyId', e.detail.value)}
                        placeholder="Select frequency"
                        disabled={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      >
                        {frequencies.map(freq => (
                          <IonSelectOption key={freq.Id} value={freq.Id}>{freq.Name}</IonSelectOption>
                        ))}
                      </IonSelect>
                      <IonInput
                        value={frequencyLevels.find(f => f.level === 'normal')?.low}
                        onIonInput={(e) => updateFrequencyLevel('normal', 'low', parseInt(e.detail.value!) || 0)}
                        type="number"
                        readonly={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      />
                      <IonInput
                        value={frequencyLevels.find(f => f.level === 'normal')?.high}
                        onIonInput={(e) => updateFrequencyLevel('normal', 'high', parseInt(e.detail.value!) || 0)}
                        type="number"
                        readonly={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      />
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--ion-color-medium)',
                      paddingLeft: '8px',
                      fontStyle: 'italic'
                    }}>
                      Avg &lt;= "Low" triggers "Increased" Level; Avg &gt;= "High" triggers "Decreased" Level
                    </div>
                  </div>

                  {/* Decreased Level */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '100px 150px 100px 100px',
                      gap: '8px',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontSize: '14px' }}>Decreased</div>
                      <IonSelect
                        value={frequencyLevels.find(f => f.level === 'decreased')?.frequencyId}
                        onIonChange={(e) => updateFrequencyLevel('decreased', 'frequencyId', e.detail.value)}
                        placeholder="Select frequency"
                        disabled={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      >
                        {frequencies.map(freq => (
                          <IonSelectOption key={freq.Id} value={freq.Id}>{freq.Name}</IonSelectOption>
                        ))}
                      </IonSelect>
                      <IonInput
                        value={frequencyLevels.find(f => f.level === 'decreased')?.low}
                        onIonInput={(e) => updateFrequencyLevel('decreased', 'low', parseInt(e.detail.value!) || 0)}
                        type="number"
                        readonly={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      />
                      <div></div>
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--ion-color-medium)',
                      paddingLeft: '8px',
                      fontStyle: 'italic'
                    }}>
                      Avg &lt; "Low" triggers return to "Normal" Level
                    </div>
                  </div>

                  {/* Increased Level */}
                  <div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '100px 150px 100px 100px',
                      gap: '8px',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontSize: '14px' }}>Increased</div>
                      <IonSelect
                        value={frequencyLevels.find(f => f.level === 'increased')?.frequencyId}
                        onIonChange={(e) => updateFrequencyLevel('increased', 'frequencyId', e.detail.value)}
                        placeholder="Select frequency"
                        disabled={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      >
                        {frequencies.map(freq => (
                          <IonSelectOption key={freq.Id} value={freq.Id}>{freq.Name}</IonSelectOption>
                        ))}
                      </IonSelect>
                      <div></div>
                      <IonInput
                        value={frequencyLevels.find(f => f.level === 'increased')?.high}
                        onIonInput={(e) => updateFrequencyLevel('increased', 'high', parseInt(e.detail.value!) || 0)}
                        type="number"
                        readonly={internalMode === 'view'}
                        style={{ fontSize: '12px', ...getReadOnlyStyle() }}
                      />
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--ion-color-medium)',
                      paddingLeft: '8px',
                      fontStyle: 'italic'
                    }}>
                      Avg &gt; "High" triggers return to "Normal" Level
                    </div>
                  </div>
                </div>
              )}

              {frequencyMode === 'fixed' && (
                <div style={{ marginTop: '20px' }}>
                  <IonText color="medium">
                    <p style={{ fontStyle: 'italic'}}>Pick and add dates</p>
                  </IonText>
                  
                  {/* Date Input Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '16px',
                    marginBottom: '20px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      border: '1px solid var(--ion-color-light-shade)',
                      borderRadius: '8px',
                      padding: '8px',
                      minWidth: '180px'
                    }}>
                      <IonDatetime
                        value={currentDate}
                        onIonChange={(e) => setCurrentDate(e.detail.value as string)}
                        presentation="date"
                        disabled={internalMode === 'view'}
                        style={{ '--padding-start': '0px', '--padding-end': '0px' }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IonCheckbox
                        checked={isCurrentDateAnnual}
                        onIonChange={(e) => setIsCurrentDateAnnual(e.detail.checked)}
                        disabled={internalMode === 'view'}
                      />
                      <IonLabel>Is annual?</IonLabel>
                    </div>
                    
                    {internalMode !== 'view' && (
                      <div style={{ textAlign: 'center', marginLeft: '16px' }}>
                        <IonButton
                          onClick={addFixedInspectionDate}
                          fill="outline"
                          size="small"
                          color="primary"
                        >
                          ADD SELECTED DATE
                        </IonButton>
                      </div>
                    )}
                  </div>

                  {/* Selected Fixed Inspection Dates */}
                  <div style={{ marginBottom: '24px' }}>
                    <IonText>
                      <h4 style={{ margin: '0 0 12px 0', color: 'var(--ion-color-medium)' }}>Selected Fixed Inspection Dates</h4>
                    </IonText>
                    
                    {fixedInspectionDates.length === 0 ? (
                      <IonText color="medium">
                        <p style={{ fontStyle: 'italic', margin: '8px 0' }}>No dates selected</p>
                      </IonText>
                    ) : (
                      <div>
                        {fixedInspectionDates.map((fixedDate, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: index < fixedInspectionDates.length - 1 ? '1px solid var(--ion-color-light-shade)' : 'none'
                            }}
                          >
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                              <span>{formatDateForDisplay(fixedDate.date)}</span>
                              <span style={{ color: 'var(--ion-color-medium)', fontSize: '14px' }}>
                                {fixedDate.isAnnual ? 'Yes' : 'No'}
                              </span>
                            </div>
                            {internalMode !== 'view' && (
                              <IonButton
                                fill="clear"
                                size="small"
                                color="danger"
                                onClick={() => removeFixedInspectionDate(index)}
                              >
                                <IonIcon icon={trashOutline} />
                              </IonButton>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Annually Recurring Header */}
                  <div style={{ marginBottom: '16px' }}>
                    <IonText>
                      <h4 style={{ margin: '0', color: 'var(--ion-color-medium)' }}>Annually Recurring</h4>
                    </IonText>
                  </div>

                  {/* Remove Old Non-annual Dates Checkbox */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <IonCheckbox
                      checked={removeOldNonAnnual}
                      onIonChange={(e) => setRemoveOldNonAnnual(e.detail.checked)}
                      disabled={internalMode === 'view'}
                    />
                    <IonLabel>Remove Old Non-annual dates</IonLabel>
                  </div>
                </div>
              )}

              {/* Scheduled Inspections */}
              <div style={{ marginTop: '20px' }}>
                <IonText>
                  <h4 style={{ margin: '0 0 12px 0', color: 'var(--ion-color-medium)' }}>SCHEDULED INSPECTIONS</h4>
                </IonText>
                
                {/* Headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  fontWeight: 'bold',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: 'var(--ion-color-medium)',
                  borderBottom: '1px solid var(--ion-color-light-shade)',
                  paddingBottom: '8px'
                }}>
                  <div>Site</div>
                  <div>Date</div>
                </div>

                {/* Scheduled Inspection Rows */}
                {scheduledInspections.length === 0 ? (
                  <IonText color="medium">
                    <p style={{ fontStyle: 'italic', margin: '8px 0' }}>No scheduled inspections</p>
                  </IonText>
                ) : (
                  <div>
                    {scheduledInspections.map((inspection, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '16px',
                          padding: '8px 0',
                          borderBottom: index < scheduledInspections.length - 1 ? '1px solid var(--ion-color-light-shade)' : 'none',
                          fontSize: '14px'
                        }}
                      >
                        <div>{inspection.site || 'test location'}</div>
                        <div>{formatDateForDisplay(inspection.date)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </IonCardContent>
          </IonCard>

          {/* Inspection Plan Background Section */}
          <IonCard style={{ width: '100%', maxWidth: '100%' }}>
            <IonCardHeader>
              <IonCardTitle>Inspection Plan Background</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {/* Primary Value Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Line Item Primary Weight *</IonLabel>
                <IonInput
                  value={formData.primaryValue}
                  onIonInput={(e) => setFormData({ ...formData, primaryValue: e.detail.value! })}
                  placeholder="Enter primary weight"
                  readonly={internalMode === 'view'}
                  style={getReadOnlyStyle()}
                  type="number"
                  min="0"
                  max="1000"
                />
              </IonItem>

              {/* Alternate Value Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Line Item Alternate Weight *</IonLabel>
                <IonInput
                  value={formData.alternateValue}
                  onIonInput={(e) => setFormData({ ...formData, alternateValue: e.detail.value! })}
                  placeholder="Enter alternate weight"
                  readonly={internalMode === 'view'}
                  style={getReadOnlyStyle()}
                  type="number"
                  min="0"
                  max="1000"
                />
              </IonItem>

              {/* Hours to Inspect Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Hours to Inspect</IonLabel>
                <IonInput
                  value={formData.timeToInspect}
                  onIonInput={(e) => setFormData({ ...formData, timeToInspect: e.detail.value! })}
                  placeholder="Enter hours to inspect"
                  readonly={internalMode === 'view'}
                  style={getReadOnlyStyle()}
                  type="number"
                  min="0"
                  max="1000"
                />
              </IonItem>

              {/* Method of Inspection Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Method of Inspection</IonLabel>
                <IonSelect
                  value={formData.inspectionMethodId}
                  onIonChange={(e) => setFormData({ ...formData, inspectionMethodId: e.detail.value })}
                  placeholder="Select inspection method"
                  disabled={internalMode === 'view' || inspectionMethods.length === 0}
                  style={getReadOnlyStyle()}
                >
                  {inspectionMethods.map((method) => (
                    <IonSelectOption key={method.Id} value={method.Id}>
                      {method.Name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              {/* Area of Expertise Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Area of Expertise</IonLabel>
                <IonSelect
                  value={formData.expertiseAreaId}
                  onIonChange={(e) => setFormData({ ...formData, expertiseAreaId: e.detail.value })}
                  placeholder="Select expertise area"
                  disabled={internalMode === 'view' || expertiseAreas.length === 0}
                  style={getReadOnlyStyle()}
                >
                  {expertiseAreas.map((area) => (
                    <IonSelectOption key={area.Id} value={area.Id}>
                      {area.Name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              {/* Sample Size Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Sample Size</IonLabel>
                <IonInput
                  value={formData.sampleSize}
                  onIonInput={(e) => setFormData({ ...formData, sampleSize: e.detail.value! })}
                  placeholder="Enter sample size (max 100 characters)"
                  readonly={internalMode === 'view'}
                  style={getReadOnlyStyle()}
                  maxlength={100}
                />
              </IonItem>

              {/* Sample Selection Procedure Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Sample Selection Procedure</IonLabel>
                <IonTextarea
                  value={formData.sampleSelection}
                  onIonInput={(e) => setFormData({ ...formData, sampleSelection: e.detail.value! })}
                  placeholder="Enter sample selection procedure (max 1000 characters)"
                  rows={3}
                  readonly={internalMode === 'view'}
                  style={getReadOnlyStyle()}
                  maxlength={1000}
                />
              </IonItem>

              {/* Additional Instructions Field */}
              <IonItem>
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Additional Instructions</IonLabel>
                <IonTextarea
                  value={formData.additionalInstructions}
                  onIonInput={(e) => setFormData({ ...formData, additionalInstructions: e.detail.value! })}
                  placeholder="Enter additional instructions (max 1000 characters)"
                  rows={3}
                  readonly={internalMode === 'view'}
                  style={getReadOnlyStyle()}
                  maxlength={1000}
                />
              </IonItem>

              <IonItem>
                <AttachmentsSection
                  attachments={attachments}
                  onAddAttachment={addAttachment}
                  onDelete={handleAttachmentDelete}
                  onFileUpdate={handleFileUpdate}
                  onLinkUpdate={handleLinkUpdate}
                  onExistingFileSelect={handleExistingFileSelect}
                  onDescriptionUpdate={handleDescriptionUpdate}
                  existingFiles={existingFiles}
                  readOnly={internalMode === 'view'}
                  title="Inspection Plan Attachments"
                />
              </IonItem>
            </IonCardContent>
          </IonCard>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              {validationErrors.map((error, index) => (
                <IonText key={index} color="danger">
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>{error}</p>
                </IonText>
              ))}
            </div>
          )}

          {internalMode === 'edit' && (
            <div style={{ marginTop: '32px' }}>
              <ModalFooter
                cancelLabel="Cancel"
                actionLabel={isLoading ? 'Saving...' : 'Save Changes'}
                cancelIcon={arrowBack}
                actionIcon={checkmarkOutline}
                onCancel={handleCancel}
                onAction={handleSave}
                isCancelDisabled={isLoading}
                isActionDisabled={isLoading}
                isActionLoading={isLoading}
              />
            </div>
          )}
        </IonContent>
      </IonModal>

      {/* Confirmation Alerts */}
      <IonAlert
        isOpen={showConfirmClose}
        onDidDismiss={() => setShowConfirmClose(false)}
        header="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        buttons={[
          {
            text: 'Keep Editing',
            role: 'cancel',
          },
          {
            text: 'Discard Changes',
            handler: () => {
              if (internalMode === 'edit') {
                // Revert to original data and go back to view mode
                setFormData(originalFormData);
                setInternalMode('view');
              } else {
                onClose();
              }
            },
          },
        ]}
      />

      <IonAlert
        isOpen={showLookupSetupAlert}
        onDidDismiss={() => setShowLookupSetupAlert(false)}
        header="Lookup Setup Required"
        message={lookupSetupAlertMessage}
        buttons={['OK']}
      />

      <Toaster
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
    </>
  );
};

export default InspectionPlanModal;